import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EyeIcon,
  PencilSimpleIcon,
  TrashIcon,
  XCircleIcon,
  UploadSimpleIcon,
  ArchiveIcon,
} from '@phosphor-icons/react';
import type { DocumentNodeWithDetailsDto } from '@monorepo/shared';
import { DocumentStatus } from '@monorepo/shared';
import type { DocumentRowActions, DocumentRowPermissions } from './documentTableTypes';

const DELETABLE_STATUSES: DocumentStatus[] = [
  DocumentStatus.DRAFT,
  DocumentStatus.PENDING_VALIDATION,
  DocumentStatus.REJECTED,
  DocumentStatus.ARCHIVED,
];

interface DocumentRowMenuProps {
  document: DocumentNodeWithDetailsDto;
  permissions: DocumentRowPermissions;
  actions: DocumentRowActions;
  onClose: () => void;
}

export const DocumentRowMenu = forwardRef<HTMLDivElement, DocumentRowMenuProps>(
  ({ document, permissions, actions, onClose }, ref) => {
    const { t } = useTranslation();

    const handleAction = (action: () => void) => {
      onClose();
      action();
    };

    const showRejectOption =
      permissions.canValidate &&
      document.status === DocumentStatus.PENDING_VALIDATION &&
      document.isCurrentUserAssignedValidator &&
      !document.hasCurrentUserValidated &&
      actions.onReject;

    const showUploadNewVersionOption =
      permissions.canUploadNewVersion &&
      document.status !== null &&
      document.status !== DocumentStatus.ARCHIVED &&
      actions.onUploadNewVersion;

    const showDeleteOption =
      permissions.canDelete &&
      document.status !== null &&
      DELETABLE_STATUSES.includes(document.status);

    const showArchiveOption =
      permissions.canArchive &&
      (document.status === DocumentStatus.VALIDATED ||
        document.status === DocumentStatus.DIFFUSED) &&
      actions.onArchive;

    return (
      <div
        ref={ref}
        className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-10 min-w-[160px]"
      >
        <button
          onClick={() => handleAction(() => actions.onOpen(document.id))}
          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
        >
          <EyeIcon size={16} />
          {t('documents.actions.open')}
        </button>

        {permissions.canRename && (
          <button
            onClick={() => handleAction(() => actions.onRename(document))}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <PencilSimpleIcon size={16} />
            {t('documents.actions.rename')}
          </button>
        )}

        {showDeleteOption && (
          <button
            onClick={() => handleAction(() => actions.onDelete(document))}
            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
          >
            <TrashIcon size={16} />
            {t('documents.actions.delete')}
          </button>
        )}

        {showRejectOption && (
          <button
            onClick={() => handleAction(() => actions.onReject!(document))}
            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
          >
            <XCircleIcon size={16} />
            {t('documents.actions.reject')}
          </button>
        )}

        {showUploadNewVersionOption && (
          <button
            onClick={() => handleAction(() => actions.onUploadNewVersion!(document))}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <UploadSimpleIcon size={16} />
            {t('documents.actions.upload_new_version')}
          </button>
        )}

        {showArchiveOption && (
          <button
            onClick={() => handleAction(() => actions.onArchive!(document))}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <ArchiveIcon size={16} />
            {t('documents.actions.archive')}
          </button>
        )}
      </div>
    );
  },
);

DocumentRowMenu.displayName = 'DocumentRowMenu';
